import React, { useState, useEffect } from 'react';
import { useLang } from '../../contexts/LanguageContext';
import { X, Check, ChevronRight, ChevronLeft, Copy, Users, MessageCircle, AlertCircle } from 'lucide-react';
import { EMZI_COLORS, EMZI_SHADOWS, EMZI_RADIUS } from '../../styles/emziCI';
import toast from '../../utils/toast';
import { secureStorage } from '../../utils/secureStorage';
import { BACKEND_URL } from '../../utils/backendUrl';

/**
 * EMZI NEXUS V6.3 - Create User Wizard (Phase 3A)
 * 2-Step wizard for creating users and optionally assigning to groups
 */

const CreateUserWizard = ({ onClose, onSuccess }) => {
  const t = useLang();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    phone_e164: '',
    role: 'executor',
    group_id: '',  // Add group selection to Step 1
    group_role: 'executor'  // Role within the group
  });
  const [createdUser, setCreatedUser] = useState(null);
  const [tempPassword, setTempPassword] = useState(null);
  const [loginUrl, setLoginUrl] = useState(null);
  const [whatsappLink, setWhatsappLink] = useState(null);
  const [errors, setErrors] = useState({});

  // Fetch groups on mount for Step 1 selection
  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      setLoadingGroups(true);
            const response = await fetch(
        `${BACKEND_URL}/api/admin-panel/functional-groups?is_active=true`,
        {
          headers: {
            'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setGroups(data);
        // Pre-select first group for better UX
        if (data.length > 0 && !formData.group_id) {
          setFormData(prev => ({ ...prev, group_id: data[0].group_id }));
        }
      }
    } catch (err) {
      console.error('Error fetching groups:', err);
    } finally {
      setLoadingGroups(false);
    }
  };

  const validateStep1 = () => {
    const newErrors = {};
    
    // Email validation
    if (!formData.email) {
      newErrors.email = t('admin.users.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('admin.users.errors.invalidEmail');
    }
    
    // Full name validation
    if (!formData.full_name) {
      newErrors.full_name = t('admin.users.nameRequired');
    } else if (formData.full_name.trim().length < 2) {
      newErrors.full_name = 'Name must be at least 2 characters';
    }
    
    // WhatsApp phone validation (E.164 format)
    if (!formData.phone_e164) {
      newErrors.phone_e164 = t('admin.users.phoneRequired');
    } else if (!/^\+[1-9]\d{1,14}$/.test(formData.phone_e164)) {
      newErrors.phone_e164 = t('admin.users.errors.invalidPhone');
    }
    
    // Team/Group validation - REQUIRED for proper workflow
    if (!formData.group_id) {
      newErrors.group_id = t('admin.users.errors.groupRequired') || 'Please select a team for this user';
    }
    
    setErrors(newErrors);
    
    // Show toast for validation errors
    if (Object.keys(newErrors).length > 0) {
      toast.error('Please fix the form errors before submitting');
    }
    
    return Object.keys(newErrors).length === 0;
  };

  const createUser = async () => {
    if (!validateStep1()) return;

    try {
      setLoading(true);
      setErrors({}); // Clear previous errors
      
            
      // Map frontend group_role to backend group_role_layer format
      const groupRoleMap = {
        'executor': 'Executor',
        'supervisor': 'Supervisor', 
        'management': 'Management',
        'Executor': 'Executor',
        'Supervisor': 'Supervisor',
        'Management': 'Management'
      };
      
      // ATOMIC: Create user WITH group assignment in single API call
      // This ensures user always has proper team_id for workflow routing
      const response = await fetch(
        `${BACKEND_URL}/api/admin-panel/users`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            email: formData.email,
            full_name: formData.full_name,
            phone_e164: formData.phone_e164,
            role: formData.role,
            // CRITICAL: Include group assignment in the same request
            assign_to_group: formData.group_id,
            group_role_layer: groupRoleMap[formData.group_role] || 'Executor'
          })
        }
      );

      const data = await response.json();

      if (response.ok) {
        const createdUserData = data.data;
        setCreatedUser(createdUserData);
        setTempPassword(createdUserData.temp_password);
        setLoginUrl(createdUserData.login_url);
        setWhatsappLink(createdUserData.whatsapp_link);
        
        const selectedGroup = groups.find(g => g.group_id === formData.group_id);
        toast.success(`✅ User created and assigned to ${selectedGroup?.name || 'team'}`);
        
        setStep(2);
      } else if (response.status === 409) {
        // Handle duplicate errors with specific messages
        const errorDetail = data.detail || {};
        const errorCode = errorDetail.error_code || '';
        
        if (errorCode === 'EMAIL_DUPLICATE') {
          setErrors({ email: t('admin.users.errors.emailDuplicate') });
          toast.error(t('admin.users.errors.emailDuplicate'));
        } else if (errorCode === 'PHONE_DUPLICATE') {
          setErrors({ phone_e164: t('admin.users.errors.phoneDuplicate') });
          toast.error(t('admin.users.errors.phoneDuplicate'));
        } else {
          const message = errorDetail.message || t('admin.users.create.errorGeneric');
          setErrors({ general: message });
          toast.error(message);
        }
      } else if (response.status === 401) {
        toast.error(t('admin.users.create.errorAuth'));
        setErrors({ general: t('admin.users.create.errorAuth') });
      } else if (response.status === 403) {
        toast.error(t('admin.users.create.errorPermission'));
        setErrors({ general: t('admin.users.create.errorPermission') });
      } else {
        // Generic error
        const message = data.detail?.message || data.detail || t('admin.users.create.errorGeneric');
        setErrors({ general: message });
        toast.error(message);
      }
    } catch (err) {
      console.error('Error creating user:', err);
      const errorMessage = t('admin.users.create.errorNetwork');
      setErrors({ general: errorMessage });
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(() => {
        toast.success(t('admin.users.passwordCopied'));
      }).catch(() => {
        fallbackCopy(text);
      });
    } else {
      fallbackCopy(text);
    }
  };

  const fallbackCopy = (text) => {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      toast.success(t('admin.users.passwordCopied'));
    } catch (e) {
      toast.error('Copy failed — please select and copy manually');
    }
  };

  const renderStep1 = () => (
    <div>
      <h3 style={{
        fontSize: '18px',
        fontWeight: '600',
        color: EMZI_COLORS.textPrimary,
        marginBottom: '24px'
      }}>
        {t('admin.users.create.step1Title')}
      </h3>

      {/* Email */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: '500',
          color: EMZI_COLORS.textPrimary,
          marginBottom: '8px'
        }}>
          {t('admin.users.email')} *
        </label>
        <div style={{ position: 'relative' }}>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="user@example.com"
            style={{
              width: '100%',
              padding: '12px',
              paddingRight: errors.email ? '40px' : '12px',
              border: `2px solid ${errors.email ? '#ef4444' : EMZI_COLORS.border}`,
              borderRadius: EMZI_RADIUS.sm,
              fontSize: '14px',
              outline: 'none',
              transition: 'border-color 0.2s'
            }}
            onFocus={(e) => {
              if (!errors.email) {
                e.target.style.borderColor = EMZI_COLORS.primary;
              }
            }}
            onBlur={(e) => {
              if (!errors.email) {
                e.target.style.borderColor = EMZI_COLORS.border;
              }
            }}
          />
          {errors.email && (
            <AlertCircle 
              size={20} 
              color="#ef4444" 
              style={{ 
                position: 'absolute', 
                right: '12px', 
                top: '50%', 
                transform: 'translateY(-50%)' 
              }} 
            />
          )}
        </div>
        {errors.email && (
          <div style={{ 
            color: '#ef4444', 
            fontSize: '12px', 
            marginTop: '6px',
            fontWeight: '500'
          }}>
            {errors.email}
          </div>
        )}
      </div>

      {/* Full Name */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: '500',
          color: EMZI_COLORS.textPrimary,
          marginBottom: '8px'
        }}>
          {t('admin.users.fullName')} *
        </label>
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            value={formData.full_name}
            onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
            placeholder="John Doe"
            style={{
              width: '100%',
              padding: '12px',
              paddingRight: errors.full_name ? '40px' : '12px',
              border: `2px solid ${errors.full_name ? '#ef4444' : EMZI_COLORS.border}`,
              borderRadius: EMZI_RADIUS.sm,
              fontSize: '14px',
              outline: 'none',
              transition: 'border-color 0.2s'
            }}
            onFocus={(e) => {
              if (!errors.full_name) {
                e.target.style.borderColor = EMZI_COLORS.primary;
              }
            }}
            onBlur={(e) => {
              if (!errors.full_name) {
                e.target.style.borderColor = EMZI_COLORS.border;
              }
            }}
          />
          {errors.full_name && (
            <AlertCircle 
              size={20} 
              color="#ef4444" 
              style={{ 
                position: 'absolute', 
                right: '12px', 
                top: '50%', 
                transform: 'translateY(-50%)' 
              }} 
            />
          )}
        </div>
        {errors.full_name && (
          <div style={{ 
            color: '#ef4444', 
            fontSize: '12px', 
            marginTop: '6px',
            fontWeight: '500'
          }}>
            {errors.full_name}
          </div>
        )}
      </div>

      {/* WhatsApp Phone Number */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: '500',
          color: EMZI_COLORS.textPrimary,
          marginBottom: '8px'
        }}>
          {t('admin.users.phoneLabel')} *
        </label>
        <div style={{ position: 'relative' }}>
          <input
            type="tel"
            value={formData.phone_e164}
            onChange={(e) => setFormData({ ...formData, phone_e164: e.target.value })}
            placeholder={t('admin.users.phonePlaceholder')}
            style={{
              width: '100%',
              padding: '12px',
              paddingRight: errors.phone_e164 ? '40px' : '12px',
              border: `2px solid ${errors.phone_e164 ? '#ef4444' : EMZI_COLORS.border}`,
              borderRadius: EMZI_RADIUS.sm,
              fontSize: '14px',
              outline: 'none',
              transition: 'border-color 0.2s'
            }}
            onFocus={(e) => {
              if (!errors.phone_e164) {
                e.target.style.borderColor = EMZI_COLORS.primary;
              }
            }}
            onBlur={(e) => {
              if (!errors.phone_e164) {
                e.target.style.borderColor = EMZI_COLORS.border;
              }
            }}
          />
          {errors.phone_e164 && (
            <AlertCircle 
              size={20} 
              color="#ef4444" 
              style={{ 
                position: 'absolute', 
                right: '12px', 
                top: '50%', 
                transform: 'translateY(-50%)' 
              }} 
            />
          )}
        </div>
        <div style={{ 
          color: errors.phone_e164 ? '#ef4444' : EMZI_COLORS.textSecondary, 
          fontSize: '12px', 
          marginTop: '6px',
          display: 'flex',
          alignItems: 'center',
          gap: '4px'
        }}>
          {errors.phone_e164 ? (
            <span style={{ fontWeight: '500' }}>{errors.phone_e164}</span>
          ) : (
            <span>{t('admin.users.create.e164Hint')}</span>
          )}
        </div>
      </div>

      {/* Team/Group Selection - REQUIRED */}
      <div style={{ marginBottom: '24px' }}>
        <label style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: '500',
          color: EMZI_COLORS.textPrimary,
          marginBottom: '8px'
        }}>
          <Users size={16} style={{ display: 'inline', marginRight: '6px', verticalAlign: 'middle' }} />
          {t('admin.users.selectTeam') || 'Assign to Team'} *
        </label>
        {loadingGroups ? (
          <div style={{
            padding: '12px',
            border: `1px solid ${EMZI_COLORS.border}`,
            borderRadius: EMZI_RADIUS.sm,
            fontSize: '14px',
            color: EMZI_COLORS.textSecondary
          }}>
            Loading teams...
          </div>
        ) : groups.length === 0 ? (
          <div style={{
            padding: '12px',
            background: '#fef3c7',
            border: '1px solid #fbbf24',
            borderRadius: EMZI_RADIUS.sm,
            fontSize: '14px',
            color: '#92400e'
          }}>
            ⚠️ No teams available. Please create a team first in Functional Groups.
          </div>
        ) : (
          <>
            <select
              value={formData.group_id}
              onChange={(e) => setFormData({ ...formData, group_id: e.target.value })}
              style={{
                width: '100%',
                padding: '12px',
                border: `2px solid ${errors.group_id ? '#ef4444' : EMZI_COLORS.primary}`,
                borderRadius: EMZI_RADIUS.sm,
                fontSize: '14px',
                cursor: 'pointer',
                background: '#f0f9ff'
              }}
            >
              <option value="">-- Select Team --</option>
              {groups.map(group => (
                <option key={group.group_id} value={group.group_id}>
                  {group.name}
                </option>
              ))}
            </select>
            {errors.group_id && (
              <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '6px', fontWeight: '500' }}>
                {errors.group_id}
              </div>
            )}
            <div style={{ 
              fontSize: '12px', 
              color: EMZI_COLORS.textSecondary, 
              marginTop: '6px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              💡 {t('admin.users.teamHint') || 'User will see submissions from this team. Supervisors can only approve items from their team.'}
            </div>
          </>
        )}
      </div>

      {/* Role */}
      <div style={{ marginBottom: '24px' }}>
        <label style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: '500',
          color: EMZI_COLORS.textPrimary,
          marginBottom: '8px'
        }}>
          {t('admin.users.role')} *
        </label>
        <select
          value={formData.role}
          onChange={(e) => setFormData({ ...formData, role: e.target.value })}
          style={{
            width: '100%',
            padding: '12px',
            border: `1px solid ${EMZI_COLORS.border}`,
            borderRadius: EMZI_RADIUS.sm,
            fontSize: '14px',
            cursor: 'pointer'
          }}
        >
          <option value="executor">{t('admin.users.roles.executor')}</option>
          <option value="supervisor">{t('admin.users.roles.supervisor')}</option>
          <option value="management_functional">{t('admin.users.roles.management_functional')}</option>
          <option value="admin">{t('admin.users.roles.admin')}</option>
        </select>
      </div>

      {errors.general && (
        <div style={{
          padding: '12px',
          background: '#fee2e2',
          color: '#991b1b',
          borderRadius: EMZI_RADIUS.sm,
          fontSize: '14px',
          marginBottom: '20px'
        }}>
          {errors.general}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
        <button
          onClick={onClose}
          style={{
            padding: '12px 24px',
            background: 'white',
            color: EMZI_COLORS.textPrimary,
            border: `1px solid ${EMZI_COLORS.border}`,
            borderRadius: EMZI_RADIUS.sm,
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          {t('admin.users.cancel')}
        </button>
        <button
          onClick={createUser}
          disabled={loading}
          style={{
            padding: '12px 24px',
            background: loading ? EMZI_COLORS.textSecondary : EMZI_COLORS.primary,
            color: 'white',
            border: 'none',
            borderRadius: EMZI_RADIUS.sm,
            fontSize: '14px',
            fontWeight: '600',
            cursor: loading ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          {loading ? t('common.saving') : t('admin.users.create.next')}
          {!loading && <ChevronRight size={16} />}
        </button>
      </div>
    </div>
  );

  const renderStep2 = () => {
    // Get the assigned team name for display
    const assignedTeam = groups.find(g => g.group_id === formData.group_id);
    const teamName = assignedTeam?.name || formData.group_id;
    
    return (
      <div>
        {/* Success Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '24px',
          padding: '20px',
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          borderRadius: EMZI_RADIUS.md,
          color: 'white'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '8px' }}>✓</div>
          <h3 style={{
            fontSize: '20px',
            fontWeight: '700',
            marginBottom: '4px'
          }}>
            {t('admin.users.create.success')}
          </h3>
          <p style={{
            fontSize: '14px',
            opacity: 0.9,
            marginBottom: '8px'
          }}>
            {createdUser?.email}
          </p>
          {/* Show assigned team */}
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 12px',
            background: 'rgba(255,255,255,0.2)',
            borderRadius: '20px',
            fontSize: '13px'
          }}>
            <Users size={14} />
            <span>{t('admin.users.assignedToTeam') || 'Ditugaskan ke'}: <strong>{teamName}</strong></span>
          </div>
        </div>

        {/* Temporary Password Section - Prominent */}
        {tempPassword && (
          <div style={{
            padding: '20px',
            background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
            border: '3px solid #fbbf24',
            borderRadius: EMZI_RADIUS.lg,
            marginBottom: '20px',
            boxShadow: EMZI_SHADOWS.md
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '12px'
            }}>
              <AlertCircle size={20} color="#92400e" />
              <div style={{
                fontSize: '15px',
                fontWeight: '700',
                color: '#92400e'
              }}>
                {t('admin.users.tempPasswordLabel')}
              </div>
            </div>
            
            {/* Password Display */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '16px',
              padding: '14px',
              background: 'white',
              borderRadius: EMZI_RADIUS.md,
              border: '2px solid #fbbf24'
            }}>
              <code style={{
                flex: 1,
                fontSize: '18px',
                fontWeight: '700',
                fontFamily: 'monospace',
                color: '#92400e',
                letterSpacing: '1px'
              }}>
                {tempPassword}
              </code>
              <button
                onClick={() => copyToClipboard(tempPassword)}
                style={{
                  padding: '10px 16px',
                  background: '#fbbf24',
                  color: '#92400e',
                  border: 'none',
                  borderRadius: EMZI_RADIUS.sm,
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'transform 0.1s',
                  minHeight: '42px'
                }}
                onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.95)'}
                onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                <Copy size={16} />
                {t('admin.users.copyPassword')}
              </button>
            </div>
            
            {/* Warning */}
            <div style={{
              fontSize: '13px',
              color: '#92400e',
              marginBottom: '16px',
              padding: '12px',
              background: 'rgba(255, 255, 255, 0.5)',
              borderRadius: EMZI_RADIUS.sm,
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px'
            }}>
              <span>⚠️</span>
              <span>{t('admin.users.mustChangeWarning')}</span>
            </div>
            
            {/* WhatsApp Share Button */}
            {whatsappLink && (
              <button
                onClick={() => window.open(whatsappLink, '_blank')}
                style={{
                  width: '100%',
                  padding: '14px',
                  background: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: EMZI_RADIUS.md,
                  fontSize: '15px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  minHeight: '52px',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#059669';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#10b981';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                }}
              >
                <MessageCircle size={20} />
                {t('admin.users.shareViaWhatsApp')}
              </button>
            )}
          </div>
        )}

        {/* Login URL Display */}
        {loginUrl && (
          <div style={{
            padding: '16px',
            background: '#f0f9ff',
            border: '2px solid #0ea5e9',
            borderRadius: EMZI_RADIUS.md,
            marginBottom: '20px'
          }}>
            <div style={{
              fontSize: '13px',
              fontWeight: '600',
              color: '#0c4a6e',
              marginBottom: '8px'
            }}>
              {t('admin.users.loginLinkLabel')}
            </div>
            <a
              href={loginUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'block',
                padding: '12px',
                background: 'white',
                border: '1px solid #0ea5e9',
                borderRadius: EMZI_RADIUS.sm,
                fontSize: '13px',
                color: '#0284c7',
                textDecoration: 'none',
                wordBreak: 'break-all',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f0f9ff'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
            >
              {loginUrl}
            </a>
          </div>
        )}

        {/* Single Done Button - No more redundant group selection */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={onSuccess}
            style={{
              padding: '14px 32px',
              background: EMZI_COLORS.primary,
              color: 'white',
              border: 'none',
              borderRadius: EMZI_RADIUS.sm,
              fontSize: '15px',
              fontWeight: '700',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
            }}
          >
            <Check size={18} />
            {t('admin.users.create.done') || 'Selesai'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: EMZI_RADIUS.lg,
        width: '100%',
        maxWidth: '500px',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: EMZI_SHADOWS.xl
      }}>
        {/* Header */}
        <div style={{
          padding: '24px',
          borderBottom: `2px solid ${EMZI_COLORS.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Users size={24} color={EMZI_COLORS.primary} />
            <div>
              <h2 style={{
                fontSize: '20px',
                fontWeight: '700',
                color: EMZI_COLORS.textPrimary,
                marginBottom: '4px'
              }}>
                {t('admin.users.create.title')}
              </h2>
              <div style={{
                fontSize: '13px',
                color: EMZI_COLORS.textSecondary
              }}>
                {step === 1 
                  ? t('admin.users.create.step1Title') 
                  : (t('admin.users.create.successSubtitle') || '✓ Pengguna Berjaya Dicipta')}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              color: EMZI_COLORS.textSecondary
            }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '24px' }}>
          {step === 1 ? renderStep1() : renderStep2()}
        </div>
      </div>
    </div>
  );
};

export default CreateUserWizard;